import { Component, inject, signal } from '@angular/core';
import { form, FormField, required } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { PlatformBillingPriceModeEnum } from '@libs/types/src/services/enum/platform-billing/platform-billing-price-mode.enum';
import { PlatformBillingServiceContractObject } from '@libs/types/src/services/object-type/platform-billing/platform-billing-organization.object';
import { PlatformBillingTariffObject, PlatformBillingTariffVersionObject } from '@libs/types/src/services/object-type/platform-billing/platform-billing-tariff.object';
import { ButtonComponent } from '../../../../../../../components/kit/button/button.component';
import { ActionButtonsComponent } from '../../../../../../../components/kit/action-buttons/action-buttons.component';
import { BaseDialogComponent } from '../../../../../../../components/kit/base-dialog/base-dialog.component';
import { BaseDialogService } from '../../../../../../../components/kit/base-dialog/base-dialog-service';
import { DateInputComponent } from '../../../../../../../components/kit/input/date-input-v2/date-input.component';
import { SelectInputComponent } from '../../../../../../../components/kit/input/select-input-v2/select-input.component';
import { TextAreaInputComponent } from '../../../../../../../components/kit/input/text-area-input-v2/text-area-input.component';
import { NumberInputComponent } from '../../../../../../../components/kit/input/text-input-v2/number-input.component';
import { successCreated, successUpdated } from '../../../../../content.error';
import { PlatformBillingContractFormModel, PlatformBillingSelectOption, PlatformBillingTariffSelectOption } from '../organization-platform-billing.types';
import { OrganizationPlatformBillingDiscountsDialogComponent } from '../organization-platform-billing-discounts-dialog/organization-platform-billing-discounts-dialog.component';
import { OrganizationPlatformBillingContractUpsertDialogStore } from './organization-platform-billing-contract-upsert-dialog.store';
import { translate } from '../../../../../../shared/shared.global-service';

@Component({
    selector: 'app-organization-platform-billing-contract-upsert-dialog',
    templateUrl: './organization-platform-billing-contract-upsert-dialog.component.html',
    styleUrl: './organization-platform-billing-contract-upsert-dialog.component.scss',
    providers: [OrganizationPlatformBillingContractUpsertDialogStore],
    imports: [FormField, TranslatePipe, SelectInputComponent, NumberInputComponent, DateInputComponent, TextAreaInputComponent, ActionButtonsComponent, ButtonComponent],
})
export class OrganizationPlatformBillingContractUpsertDialogComponent extends BaseDialogComponent {
    protected readonly store = inject(OrganizationPlatformBillingContractUpsertDialogStore);
    private readonly dialogService = inject(BaseDialogService);
    protected readonly item = this.dialogConfig.data.item as PlatformBillingServiceContractObject | null;
    protected readonly organizationId = this.dialogConfig.data.organizationId as number;
    protected readonly serviceOptions = this.dialogConfig.data.serviceOptions as PlatformBillingSelectOption[];
    protected readonly tariffOptions = this.dialogConfig.data.tariffOptions as PlatformBillingTariffSelectOption[];
    protected readonly priceModeOptions = [
        { value: PlatformBillingPriceModeEnum.FIXED, name: 'Pevná cena za službu' },
        { value: PlatformBillingPriceModeEnum.PER_USER, name: 'Měsíční cena za uživatele' },
    ];
    protected readonly prefix = 'organization.platformBilling.fields.';
    private discountsChanged = false;

    protected readonly formData = signal<PlatformBillingContractFormModel>({
        id: this.item?.id ?? null,
        serviceId: this.item?.serviceId ?? null,
        tariffId: this.item?.tariffId ?? null,
        serviceTypeName: this.item?.serviceTypeName ?? null,
        serviceName: this.item?.serviceName ?? null,
        priceMode: (this.item?.priceMode as PlatformBillingPriceModeEnum | null | undefined) ?? null,
        fixedPriceOverride: this.item?.fixedPriceOverride ?? null,
        monthlyUserPriceOverride: this.item?.monthlyUserPriceOverride ?? null,
        validFrom: this.item ? this.toDate(this.item.validFrom) : new Date(),
        validTo: this.toDate(this.item?.validTo ?? null),
        note: this.item?.note ?? '',
    });
    protected readonly formDef = form(this.formData, (schema) => {
        required(schema.serviceId);
        required(schema.priceMode, {
            when: ({ valueOf }) => valueOf(schema.tariffId) !== null || valueOf(schema.validFrom) !== null,
        });
        required(schema.validFrom, {
            when: ({ valueOf }) => valueOf(schema.tariffId) !== null || valueOf(schema.priceMode) !== null,
        });
        required(schema.fixedPriceOverride, {
            when: ({ valueOf }) => valueOf(schema.tariffId) === null && valueOf(schema.priceMode) === PlatformBillingPriceModeEnum.FIXED,
        });
        required(schema.monthlyUserPriceOverride, {
            when: ({ valueOf }) => valueOf(schema.tariffId) === null && valueOf(schema.priceMode) === PlatformBillingPriceModeEnum.PER_USER,
        });
    });

    protected applyTariff(selection: unknown): void {
        const tariff = this.selectedTariff(selection);
        if (!tariff) return;

        const version = this.currentTariffVersion(tariff);
        if (!version) return;
        const priceMode = this.tariffPriceMode(version, this.formData().priceMode);
        this.applyTariffPrice(version, priceMode);
    }

    protected applyPriceMode(selection: unknown): void {
        const priceMode = this.selectedPriceMode(selection);
        const tariffId = this.formData().tariffId;
        const tariff = this.tariffOptions.find((option) => option.value === tariffId)?.tariff;
        const version = tariff ? this.currentTariffVersion(tariff) : null;
        if (!version) {
            this.formData.update((current) => ({
                ...current,
                priceMode,
                fixedPriceOverride: priceMode === PlatformBillingPriceModeEnum.FIXED ? current.fixedPriceOverride : null,
                monthlyUserPriceOverride: priceMode === PlatformBillingPriceModeEnum.PER_USER ? current.monthlyUserPriceOverride : null,
            }));
            return;
        }
        this.applyTariffPrice(version, priceMode);
    }

    protected openDiscountsDialog(): void {
        if (!this.item) return;
        this.dialogService.open(OrganizationPlatformBillingDiscountsDialogComponent, {
            header: translate.instant('organization.platformBilling.tabs.discounts'),
            styleClass: 'p-dialog-width-60',
            data: {
                organizationId: this.organizationId,
                contractId: this.item.id,
            },
        }).onClose.subscribe((changed) => {
            this.discountsChanged ||= Boolean(changed);
        });
    }

    protected close(): void {
        this.dialogRef.close(this.discountsChanged);
    }

    public override async save(): Promise<void> {
        if (this.formDef().invalid()) {
            this.formDef().markAsTouched();
            return;
        }

        const value = this.formData();
        if (value.serviceId === null) return;
        const input = {
            organizationId: this.organizationId,
            serviceId: value.serviceId,
            tariffId: value.tariffId,
            serviceTypeName: value.serviceTypeName,
            serviceName: value.serviceName,
            priceMode: value.priceMode,
            fixedPriceOverride: value.priceMode === PlatformBillingPriceModeEnum.FIXED ? value.fixedPriceOverride : null,
            monthlyUserPriceOverride: value.priceMode === PlatformBillingPriceModeEnum.PER_USER ? value.monthlyUserPriceOverride : null,
            validFrom: this.toDateString(value.validFrom),
            validTo: this.toDateString(value.validTo),
            note: value.note || null,
        };
        if (this.item) {
            await this.store.api.updatePlatformBillingContract({ id: this.item.id, ...input });
            successUpdated();
        } else {
            await this.store.api.createPlatformBillingContract(input);
            successCreated();
        }
        this.dialogRef.close(true);
    }

    private toDate(value: string | null): Date | null {
        return value ? new Date(`${value}T12:00:00`) : null;
    }

    private selectedTariff(selection: unknown): PlatformBillingTariffObject | null {
        if (!selection || typeof selection !== 'object' || !('value' in selection)) return null;
        const tariffId = (selection as { value?: unknown }).value;
        return typeof tariffId === 'number'
            ? this.tariffOptions.find((option) => option.value === tariffId)?.tariff ?? null
            : null;
    }

    private selectedPriceMode(selection: unknown): PlatformBillingPriceModeEnum | null {
        if (!selection || typeof selection !== 'object' || !('value' in selection)) return null;
        const value = (selection as { value?: unknown }).value;
        return value === PlatformBillingPriceModeEnum.FIXED || value === PlatformBillingPriceModeEnum.PER_USER
            ? value
            : null;
    }

    private currentTariffVersion(tariff: PlatformBillingTariffObject): PlatformBillingTariffVersionObject | null {
        const referenceDate = this.toDateString(this.formData().validFrom) ?? this.toDateString(new Date());
        if (!referenceDate) return tariff.versions[0] ?? null;
        return tariff.versions.find((version) => version.validFrom <= referenceDate
            && (!version.validTo || version.validTo >= referenceDate)) ?? tariff.versions[0] ?? null;
    }

    private tariffPriceMode(
        version: PlatformBillingTariffVersionObject,
        current: PlatformBillingPriceModeEnum | null,
    ): PlatformBillingPriceModeEnum | null {
        if (current === PlatformBillingPriceModeEnum.FIXED && version.fixedServicePrice !== null) return current;
        if (current === PlatformBillingPriceModeEnum.PER_USER && version.monthlyUserPrice !== null) return current;
        if (version.fixedServicePrice !== null && version.monthlyUserPrice === null) return PlatformBillingPriceModeEnum.FIXED;
        if (version.monthlyUserPrice !== null && version.fixedServicePrice === null) return PlatformBillingPriceModeEnum.PER_USER;
        return null;
    }

    private applyTariffPrice(
        version: PlatformBillingTariffVersionObject,
        priceMode: PlatformBillingPriceModeEnum | null,
    ): void {
        this.formData.update((current) => ({
            ...current,
            priceMode,
            fixedPriceOverride: priceMode === PlatformBillingPriceModeEnum.FIXED ? version.fixedServicePrice : null,
            monthlyUserPriceOverride: priceMode === PlatformBillingPriceModeEnum.PER_USER ? version.monthlyUserPrice : null,
        }));
    }

    private toDateString(value: Date | null): string | null {
        if (!value) return null;
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
